import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FaCogs, FaEdit, FaSave } from "react-icons/fa";
import { getApiBase } from "../apiBase";
import RecomSystem from "./RecomSystem";

const ConfigInput = ({ label, value, editable, tooltip, onChange, type = "number" }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-semibold text-[#4B334F]" title={tooltip}>{label}</label>
    {editable ? (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-[#D4C7D8] rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#5B3A60] outline-none transition"
      />
    ) : (
      <div className="border border-[#E2D7E2] bg-[#FBF8FA] rounded-lg px-3 py-2 text-sm text-[#5B3A60] break-all">
        {value ?? "—"}
      </div>
    )}
  </div>
);

const DropdownInput = ({ label, value, editable, tooltip, onChange, options }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-semibold text-[#4B334F]" title={tooltip}>{label}</label>
    {editable ? (
      <select value={value} onChange={(e) => onChange(e.target.value)} className="border border-[#D4C7D8] rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#5B3A60] outline-none transition">
        {options.map((opt) => <option key={opt}>{opt}</option>)}
      </select>
    ) : (
      <div className="border border-[#E2D7E2] bg-[#FBF8FA] rounded-lg px-3 py-2 text-sm text-[#5B3A60]">{value ?? "—"}</div>
    )}
  </div>
);

const Toggle = ({ value, onChange, disabled }) => (
  <div
    onClick={() => !disabled && onChange(!value)}
    className={`w-12 h-6 flex items-center rounded-full p-1 cursor-pointer transition ${value ? "bg-[#5B3A60]" : "bg-gray-300"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
  >
    <div className={`bg-white w-5 h-5 rounded-full shadow transform transition ${value ? "translate-x-6" : "translate-x-0"}`} />
  </div>
);

export default function BotConfig() {
  const { tenantId, botId } = useParams();
  const [configState, setConfigState] = useState({});
  const [original, setOriginal] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/${botId}/config`, {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) return;
        const data = await res.json();
        if (!res.ok) throw new Error("Failed");
        const retrieval = data?.configuration?.retrieval || {};
        const channels = data?.configuration?.channels_enabled || {};
        const cfg = {
          llm_model: data?.configuration?.llm_model ?? "openai",
          mode: retrieval?.mode ?? "hybrid",
          k_dense: retrieval?.k_dense ?? 10,
          k_sparse: retrieval?.k_sparse ?? 10,
          rrf_k: retrieval?.rrf_k ?? 60,
          top_k_rrf: retrieval?.top_k_rrf ?? 20,
          top_k_final: retrieval?.top_k_final ?? 10,
          session_timeout_minutes: data?.configuration?.session_timeout_minutes ?? 30,
          leads_enabled: channels?.lead_capture ?? false,
          lead_form_trigger_after_messages: data?.configuration?.lead_form_trigger_after_messages ?? 2,
        };
        setConfigState(cfg);
        setOriginal(cfg);
      } catch (err) {
        console.error("Load config error:", err);
      }
    };
    load();
  }, [tenantId, botId]);

  const handleSave = async () => {
    if (!editing) {
      setEditing(true);
      return;
    }
    try {
      setSaving(true);
      const payload = {};
      if (configState.llm_model !== original.llm_model) payload.llm_model = configState.llm_model;
      const RETRIEVAL_DEFAULTS = { k_dense: 10, k_sparse: 10, rrf_k: 60, top_k_rrf: 50, top_k_final: 10 };
      const rc = {};
      if (configState.mode !== original.mode) rc.mode = configState.mode || "hybrid";
      ["k_dense", "k_sparse", "rrf_k", "top_k_rrf", "top_k_final"].forEach((f) => {
        const rawNew = configState[f];
        const rawOld = original[f];
        const nv = (rawNew === "" || rawNew === null || rawNew === undefined || Number(rawNew) === 0) ? RETRIEVAL_DEFAULTS[f] : Number(rawNew);
        const ov = (rawOld === "" || rawOld === null || rawOld === undefined || Number(rawOld) === 0) ? RETRIEVAL_DEFAULTS[f] : Number(rawOld);
        if (nv !== ov) rc[f] = nv;
      });
      if (Object.keys(rc).length) payload.retrieval = rc;
      const nt = (!configState.session_timeout_minutes || Number(configState.session_timeout_minutes) === 0) ? 30 : Number(configState.session_timeout_minutes);
      const ot = (!original.session_timeout_minutes || Number(original.session_timeout_minutes) === 0) ? 30 : Number(original.session_timeout_minutes);
      if (nt !== ot) payload.session_timeout_minutes = nt;
      const nl = (!configState.lead_form_trigger_after_messages || Number(configState.lead_form_trigger_after_messages) === 0) ? 2 : Number(configState.lead_form_trigger_after_messages);
      const ol = (!original.lead_form_trigger_after_messages || Number(original.lead_form_trigger_after_messages) === 0) ? 2 : Number(original.lead_form_trigger_after_messages);
      if (nl !== ol) payload.lead_form_trigger_after_messages = nl;
      if (configState.leads_enabled !== original.leads_enabled) payload.channels_enabled = { web_chat: true, lead_capture: configState.leads_enabled };

      if (!Object.keys(payload).length) {
        setEditing(false);
        setSaving(false);
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) return;
      const response = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/${botId}/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Failed to update");
      const result = await response.json();
      if (result?.status === "success") {
        setOriginal(configState);
        setEditing(false);
      }
    } catch {
      console.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F4F2] px-10 py-10">
      <div className="flex items-center justify-between mb-10">
        <h1 className="text-3xl font-extrabold text-[#5B3A60] flex items-center gap-2">
          <FaCogs /> Agent Configuration
        </h1>
        <div className="flex items-center gap-3">
          <button onClick={handleSave} disabled={saving} className="px-7 py-2.5 rounded-xl bg-[#5B3A60] text-white shadow-md hover:bg-[#462B4B] transition flex items-center gap-2 font-semibold text-sm disabled:opacity-50">
            {editing ? <FaSave /> : <FaEdit />}
            {saving ? "Saving..." : editing ? "Save" : "Edit"}
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-lg border border-[#E5DCE7] p-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <DropdownInput label="LLM Model" value={configState.llm_model} editable={editing} onChange={(v) => setConfigState((p) => ({ ...p, llm_model: v }))} options={["openai", "groq"]} tooltip="Underlying AI model" />
          <DropdownInput label="Retrieval Mode" value={configState.mode} editable={editing} onChange={(v) => setConfigState((p) => ({ ...p, mode: v }))} options={["hybrid", "dense", "sparse"]} tooltip="Search strategy" />
          <ConfigInput label="Top K Dense" value={configState.k_dense} editable={editing} onChange={(v) => setConfigState((p) => ({ ...p, k_dense: v }))} tooltip="Dense vector ranking" />
          <ConfigInput label="Top K Sparse" value={configState.k_sparse} editable={editing} onChange={(v) => setConfigState((p) => ({ ...p, k_sparse: v }))} tooltip="Keyword ranking" />
          <ConfigInput label="RRF K" value={configState.rrf_k} editable={editing} onChange={(v) => setConfigState((p) => ({ ...p, rrf_k: v }))} tooltip="Reciprocal Rank Fusion" />
          <ConfigInput label="Top K RRF" value={configState.top_k_rrf} editable={editing} onChange={(v) => setConfigState((p) => ({ ...p, top_k_rrf: v }))} tooltip="Documents kept after RRF" />
          <ConfigInput label="Top K Final" value={configState.top_k_final} editable={editing} onChange={(v) => setConfigState((p) => ({ ...p, top_k_final: v }))} tooltip="Final results" />
          <ConfigInput label="Session Timeout (min)" value={configState.session_timeout_minutes} editable={editing} onChange={(v) => setConfigState((p) => ({ ...p, session_timeout_minutes: v }))} tooltip="Session expiry" />
          <ConfigInput label="Lead Form" value={configState.lead_form_trigger_after_messages} editable={editing} tooltip="Show lead form after X user messages" onChange={(value) => setConfigState((prev) => ({ ...prev, lead_form_trigger_after_messages: value }))} />
          <div className="flex flex-col gap-3 md:col-span-2">
            <label className="text-xs font-semibold text-[#4B334F]">Enable Leads</label>
            {editing ? (
              <Toggle value={configState.leads_enabled} onChange={(v) => setConfigState((p) => ({ ...p, leads_enabled: v }))} />
            ) : (
              <div className="flex items-center gap-3 text-[#5B3A60]">
                <Toggle value={configState.leads_enabled} disabled />
                {configState.leads_enabled ? "Enabled" : "Disabled"}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}